import { z } from 'zod';
import { OpeningHoursSchedule } from '../opening-hours.js';
import { PortalStep } from '../portal-steps.js';
import { StorageTerms } from '../storage-terms.js';
import { BrandMode, HexColor, Id, InterventionStatus, Timestamp, amount } from './common.js';
import { PortalComplementOutput } from './complements.js';
import { NextDueOutput, WorkItemOutput } from './dossiers.js';
import { PortalMediaOutput } from './media.js';
import { PortalMessageOutput } from './messages.js';
import { EntryReportAcknowledgmentOutput } from './reception.js';

/** Lien portail créé par le garage (à partager) : l'URL ne contient que le jeton, dans le fragment. */
export const PortalLinkOutput = z.object({ url: z.string(), expiresAt: Timestamp });

/**
 * 02/10/2026 : « Renvoyer le lien de suivi par e-mail » (`portalLinks.sendByEmail`, 202). L'e-mail part du
 * worker (L4) avec un **nouveau** jeton propre à l'e-mail ; la réponse ne porte ni lien ni jeton.
 * `sentTo` : adresse masquée (« j***@exemple.fr ») ; `queuedAt` : instant d'inscription (outbox).
 */
export const PortalLinkEmailOutput = z.object({ sentTo: z.string(), queuedAt: Timestamp });
export type PortalLinkEmailOutput = z.infer<typeof PortalLinkEmailOutput>;

/** Limite du renvoi du lien de suivi par e-mail : par dossier, sur une heure glissante (429 au-delà). */
export const PORTAL_LINK_EMAIL_LIMITS = { perHourPerIntervention: 3 } as const;

export const PortalBranding = z.object({
  address: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  displayName: z.string(),
  primaryColor: HexColor,
  secondaryColor: HexColor,
  logoUrl: z.string().nullable(),
  /**
   * Correctifs (passe de design) : icône carrée du garage (adresse publique non devinable, comme le logo),
   * affichée par le portail à la place du monogramme ; `null` sans icône. Facultatif pour les maquettes du
   * web ; toujours renseigné par l'API.
   */
  iconUrl: z.string().nullable().optional(),
  /** Vague 6 : le portail suit le mode de marque du garage (valeurs par défaut tant que L1 ne les renseigne pas). */
  brandMode: BrandMode.optional(),
  tertiaryColor: HexColor.nullable().optional(),
  /**
   * Vague 7 : bloc contact (« Nous trouver ») : horaires structurés, lien d'itinéraire (`directionsUrl` de
   * l'adresse publique, sans clé ni suivi) et état d'ouverture calculé à la lecture (`openingStatus`, fuseau
   * du garage) : `openNow`, `opensAt` (fermé : prochaine ouverture), `closesAt` (ouvert : fin de la plage).
   * `null` sans horaires structurés. Obligatoires depuis l'intégration de la vague 7.
   */
  openingHoursSchedule: OpeningHoursSchedule.nullable(),
  mapsUrl: z.string().nullable(),
  openNow: z.boolean().nullable(),
  opensAt: Timestamp.nullable(),
  closesAt: Timestamp.nullable(),
});

/**
 * `active` : lien de suivi (décisions et questions possibles).
 * `report` : compte rendu permanent remis à la sortie, en lecture seule (aucune décision ni question).
 */
export const PortalMode = z.enum(['active', 'report']);

/**
 * Portail centré sur l'intervention en cours (décision du porteur, 02/10/2026) : la page client ne
 * présente **que l'intervention liée au lien**, jamais les passages précédents du véhicule (l'historique
 * viendra avec un compte client ; l'export des entretiens sera une fonction payante, phase ultérieure).
 *
 * Onglets, dans l'ordre (la réponse `portal.view` les alimente, l'interface les présente à la marque) :
 * - `summary` (Suivi) : `intervention` (statut, dates, kilométrages), `steps`, `vehicle`, `garage` (contact) ;
 * - `reception` (Réception) : `reception` (fiche signée ; contenu par `portal.entryReport`, PDF par
 *   `portal.entryReportPdf`) ;
 * - `work` (Travaux) : `work` (travaux prévus, montant estimé, travaux réalisés), `complements`, `media` ;
 * - `messages` (Messages) : `messages`, `unreadMessages` (pastille ; lecture par `portal.markMessagesRead`).
 */
export const PortalTab = z.enum(['summary', 'reception', 'work', 'messages']);
export type PortalTab = z.infer<typeof PortalTab>;
export const PORTAL_TABS: readonly PortalTab[] = PortalTab.options;
export const PORTAL_TAB_LABELS: Record<PortalTab, string> = {
  summary: 'Suivi',
  reception: 'Réception',
  work: 'Travaux',
  messages: 'Messages',
};

/** Mention affichée avec le montant estimé : indicatif, jamais une facture (Pennylane fait foi). */
export const PORTAL_ESTIMATE_NOTICE =
  'Estimation indicative, non contractuelle. Le montant définitif figure sur la facture du garage.';

/**
 * Fiche de réception vue au portail (onglet Réception) : dernière version **validée et partagée** du
 * dossier du lien, sans contenu (lu par `portal.entryReport`) ; `null` si aucune n'a été partagée.
 * `signed` : signature dessinée à l'accueil (dans le PDF) ; `acknowledgment` : prise de connaissance.
 */
export const PortalReceptionOutput = z.object({
  number: z.int(),
  version: z.int(),
  validatedAt: Timestamp,
  signed: z.boolean(),
  pdfAvailable: z.boolean(),
  acknowledgment: EntryReportAcknowledgmentOutput.nullable(),
  acknowledgeable: z.boolean(),
});
export type PortalReceptionOutput = z.infer<typeof PortalReceptionOutput>;

/**
 * Onglet Travaux, pour l'intervention du lien seulement. `plannedWork` : récapitulatif rédigé par le
 * garage ; `estimate` : montant estimé TTC avec sa mention (`PORTAL_ESTIMATE_NOTICE`), `null` sans
 * estimation (le montant est retiré de l'aperçu d'un membre sans `amounts.view`) ; `done` : travaux
 * réalisés de **cette** intervention (compte rendu), total TTC exact côté serveur.
 */
export const PortalWorkOutput = z.object({
  plannedWork: z.string().nullable(),
  estimate: z.object({ totalTtc: amount(), notice: z.string() }).nullable(),
  done: z.object({ items: z.array(WorkItemOutput), totalTtc: amount() }),
});
export type PortalWorkOutput = z.infer<typeof PortalWorkOutput>;

export const PortalViewOutput = z.object({
  mode: PortalMode,
  garage: PortalBranding,
  intervention: z.object({
    status: InterventionStatus,
    receivedAt: Timestamp,
    updatedAt: Timestamp,
    readyAt: Timestamp.nullable(),
    freeUntil: Timestamp.nullable(),
    releasedAt: Timestamp.nullable(),
    customerRequest: z.string(),
    storageTerms: StorageTerms,
    /** Fuseau du garage, pour afficher les dates comme le garage les a fixées. */
    timezone: z.string(),
    /** Gardiennage offert par le garage (exonération) : affiché au client. */
    storageWaived: z.boolean(),
    /** 02/10/2026 : kilométrage relevé à l'entrée du véhicule. */
    entryMileage: z.int(),
    /** 02/10/2026 : kilométrage relevé à la restitution, `null` tant que le véhicule n'est pas restitué. */
    exitMileage: z.int().nullable(),
    /** 08/10/2026 : préconisations et prochaine échéance du compte rendu (`null` si absentes). */
    recommendations: z.string().nullable().optional(),
    nextDue: NextDueOutput.nullable().optional(),
  }),
  /**
   * `photo` (vague 4) : photo du véhicule, copie sans EXIF ni GPS, servie par `portal.vehiclePhoto` ;
   * `null` si le réglage `showVehiclePhotoOnPortal` est désactivé (défaut) ou sans photo.
   */
  vehicle: z.object({
    plate: z.string(),
    make: z.string(),
    model: z.string(),
    version: z.string().nullable(),
    photo: z.object({ id: Id }).nullable(),
  }),
  media: z.array(PortalMediaOutput),
  complements: z.array(PortalComplementOutput),
  messages: z.array(PortalMessageOutput),
  /**
   * 02/10/2026 : messages du garage et événements du dossier (auteur `garage` ou `system`) postérieurs à la
   * dernière lecture enregistrée par `portal.markMessagesRead`. La lecture est rattachée à **l'intervention**
   * (pas à l'appareil ni au lien) ; `0` en mode `report` (compte rendu sans messagerie).
   */
  unreadMessages: z.int().nonnegative(),
  /** Onglet Réception (02/10/2026) : `null` si aucune fiche validée n'a été partagée. */
  reception: PortalReceptionOutput.nullable(),
  /** Onglet Travaux (02/10/2026). */
  work: PortalWorkOutput,
  /**
   * Vague 6 : adresse de la politique de confidentialité du garage (lien « Confidentialité » en pied
   * du portail), `garagePrivacyUrl(tx, config)` du cœur, `null` sans politique publiée.
   */
  privacyUrl: z.string().nullable(),
  /** Vague 7 : frise d'étapes du client (`buildPortalSteps`), dans l'ordre. */
  steps: z.array(PortalStep),
});
export type PortalViewOutput = z.infer<typeof PortalViewOutput>;

/**
 * 02/10/2026 : lecture des messages par le client, à l'ouverture de l'onglet Messages (`portal.markMessagesRead`).
 * `lastMessageId` : dernier message affiché (de ce dossier, sinon 404) ; absent : tous les messages actuels.
 * Idempotent : la lecture n'avance jamais en arrière (un rejeu ou un message plus ancien ne change rien).
 */
export const PortalMessagesReadInput = z.strictObject({ lastMessageId: Id.optional() });
export type PortalMessagesReadInput = z.infer<typeof PortalMessagesReadInput>;

/** `readAt` : instant du dernier message lu (horloge du serveur), `null` si rien n'a encore été lu. */
export const PortalMessagesReadOutput = z.object({
  readAt: Timestamp.nullable(),
  unreadMessages: z.int().nonnegative(),
});
export type PortalMessagesReadOutput = z.infer<typeof PortalMessagesReadOutput>;

/**
 * Vague 7 : « Voir ce que voit mon client » (`interventions.portalPreview`). La vue du portail **telle que
 * le client la voit** (même construction que `portal.view`, mode `active` si le dossier n'est pas clôturé,
 * sinon `report`), en lecture seule et **sans émettre de jeton**. Les montants de la vue sont ceux du
 * client : retirés par le serveur sans `amounts.view` (`amountsHidden` vrai, l'interface le signale).
 * Le QR code n'est jamais ici : il encode l'adresse renvoyée par `portalLinks.create` (`portal.manage`),
 * générée à la demande dans le navigateur.
 */
export const PortalPreviewOutput = z.object({
  view: PortalViewOutput,
  amountsHidden: z.boolean(),
  /** Un lien de suivi actif existe pour ce dossier (sinon, « Partager le lien » en crée un). */
  hasActiveLink: z.boolean(),
  generatedAt: Timestamp,
});
export type PortalPreviewOutput = z.infer<typeof PortalPreviewOutput>;
